import { useMemo, useState } from "react";
import Drawer from "react-modern-drawer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { object, string } from "yup";
import { HugeiconsIcon } from "@hugeicons/react";
import { instance } from "api/apiBase";
import {
  AlertCircleIcon,
  Cancel01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  loadPropertyOptions,
  loadUnitOptions
} from "api/landlord/asyncUnitProperties";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  InputSize,
  Show,
  TextArea
} from "shared/components";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { tenDigitTelParser } from "shared/components/forms/BaseInput/parsers";
import { getErrorMessage } from "utils/error";
import {
  REVIEW_ITEMS_PENDING_QUERY_KEY,
  type ReviewItem,
  type ReviewReason
} from "shared/app/LeadsTableView/slices/ReviewQueueTable/types";

interface ReviewForm {
  name: string;
  email: string;
  phone: string;
  message: string;
  property_id?: number;
  unit_id?: number;
}

const isValidUsPhone = (raw: string): boolean => {
  const digits = raw.replace(/\D/g, "");
  return (
    digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))
  );
};

const EMAIL_SCHEMA = string().email();

// Everything except the message is required to convert a review item into a lead.
const VALIDATION_SCHEMA = object({
  name: string().trim().required("Name is required"),
  email: string()
    .trim()
    .required("Email is required")
    .email("Enter a valid email"),
  phone: string()
    .required("Phone is required")
    .test("valid-us-phone", "Enter a valid US phone number", (phone) =>
      isValidUsPhone(phone ?? "")
    ),
  property_id: string().required("Select a property"),
  unit_id: string().required("Select a site")
});

const REASON_BADGE: Record<
  ReviewReason,
  { label: string; variant: BadgeVariant }
> = {
  no_property_match: {
    label: "No property match",
    variant: BadgeVariant.WARNING
  },
  property_not_found: {
    label: "Unsure which property",
    variant: BadgeVariant.ERROR
  },
  no_unit_match: {
    label: "Pick a unit",
    variant: BadgeVariant.WARNING
  },
  no_contact_method: {
    label: "No contact info found",
    variant: BadgeVariant.ERROR
  },
  incomplete_contact: {
    label: "Missing email or phone",
    variant: BadgeVariant.ERROR
  }
};

const fullNameOf = (item: ReviewItem) =>
  [item.first_name, item.last_name].filter(Boolean).join(" ").trim();

const WHITESPACE_RE = /\s+/;

// Reduce any inbound phone (leading US country "1", junk digits) to the bare 10-digit
// US number the tenDigitTelParser field expects.
const toTenDigits = (raw: string): string => {
  const digits = raw.replace(/\D/g, "");
  return (
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits
  ).slice(0, 10);
};

const splitName = (name: string) => {
  const tokens = name.trim().split(WHITESPACE_RE).filter(Boolean);
  return {
    first_name: tokens[0] ?? "",
    last_name: tokens.slice(1).join(" ")
  };
};

const warningNoteFor = (item: ReviewItem): string => {
  switch (item.reason) {
    case "property_not_found":
      return "AI wasn't sure which property this inquiry is about — we pre-filled its best guess below. Confirm it or pick the right one.";
    case "no_property_match":
      return item.property_hint
        ? `AI couldn't match "${item.property_hint}" to any property in your portfolio — could be a typo, or the property isn't onboarded yet.`
        : "AI couldn't match this inquiry to any property in your portfolio — could be a typo, or the property isn't onboarded yet.";
    case "no_unit_match":
      return "AI matched the property but not a specific unit — pick the site below to create the lead.";
    case "no_contact_method":
      return "AI couldn't find a phone or email — add both to create the lead.";
    case "incomplete_contact":
      return "AI found only one of email/phone — add the missing one to create the lead.";
    default:
      return "This item needs review before it can become a lead.";
  }
};

const initialValuesFor = (item: ReviewItem): ReviewForm => ({
  name: fullNameOf(item),
  email: item.email ?? "",
  phone: toTenDigits(item.phone ?? ""),
  message: item.message ?? "",
  property_id: item.best_guess_property_id ?? undefined,
  unit_id: item.best_guess_unit_id ?? undefined
});

const ReviewParsedEmailForm = ({
  item,
  onClose
}: {
  item: ReviewItem;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const { Unit, Property } = useUnitsPropertiesDictionary();

  const initialValues = useMemo<ReviewForm>(
    () => initialValuesFor(item),
    [item]
  );

  const { values, errors, register, handleChange, onSubmit } =
    useFormTyped<ReviewForm>(initialValues, VALIDATION_SCHEMA);

  const [propertyLabel, setPropertyLabel] = useState<string | undefined>(
    item.best_guess_property_name ?? undefined
  );
  const [unitLabel, setUnitLabel] = useState<string | undefined>(
    item.best_guess_unit_name ?? undefined
  );

  const refetchPending = () =>
    queryClient.refetchQueries({ queryKey: REVIEW_ITEMS_PENDING_QUERY_KEY });

  const { mutate: convert, isPending: isConverting } = useMutation({
    mutationKey: ["LEAD_INGEST_REVIEW_ITEM_CONVERT"],
    mutationFn: (payload: ReviewForm) => {
      // Splitting a joined name corrupts multi-word first names (e.g. "Mary Jane").
      // Only re-split when the staffer actually edited the name; otherwise keep the
      // item's original first/last as parsed.
      const nameUnchanged = payload.name.trim() === fullNameOf(item);
      const { first_name, last_name } = nameUnchanged
        ? { first_name: item.first_name ?? "", last_name: item.last_name ?? "" }
        : splitName(payload.name);
      return instance.post(
        `/portal/v1/boom_crm/lead_ingest_review_items/${item.id}/convert`,
        {
          property_id: payload.property_id,
          unit_id: payload.unit_id,
          // Send verbatim (incl. "") so an intentionally-cleared field actually clears
          // instead of the server's `|| fallback` resurrecting the stale parsed value.
          first_name,
          last_name,
          email: payload.email,
          phone: payload.phone,
          message: payload.message
        }
      );
    },
    onSuccess: () => {
      toast.success("Lead created");
      onClose();
      return refetchPending();
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const { mutate: dismiss, isPending: isDismissing } = useMutation({
    mutationKey: ["LEAD_INGEST_REVIEW_ITEM_DISMISS"],
    mutationFn: () =>
      instance.post(
        `/portal/v1/boom_crm/lead_ingest_review_items/${item.id}/dismiss`
      ),
    onSuccess: () => {
      toast.success("Item dismissed");
      onClose();
      return refetchPending();
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const isBusy = isConverting || isDismissing;

  // Everything but the message is required. Validate live so the Create-lead button
  // reflects validity and a half-typed phone/email shows its error immediately.
  const emailValid = EMAIL_SCHEMA.isValidSync(values.email.trim());
  const phoneValid = isValidUsPhone(values.phone);
  const canSubmit =
    Boolean(values.name.trim()) &&
    Boolean(values.email.trim()) &&
    emailValid &&
    phoneValid &&
    Boolean(values.property_id) &&
    Boolean(values.unit_id);
  const phoneError =
    values.phone && !phoneValid
      ? "Enter a valid US phone number"
      : errors.phone;
  const emailError =
    values.email.trim() && !emailValid ? "Enter a valid email" : errors.email;

  const reasonBadge = REASON_BADGE[item.reason] ?? {
    label: item.reason,
    variant: BadgeVariant.BASE
  };

  // Anchor the AI warning to the field the staffer must actually fix:
  // a missing-contact flag belongs beside Email/Phone, a property flag beside Property.
  const warningTarget: "contact" | "property" =
    item.reason === "no_contact_method" || item.reason === "incomplete_contact"
      ? "contact"
      : "property";

  const warningBox = (
    <div className="flex items-start gap-2 rounded-lg border border-warning-100 bg-warning-50 p-3 text-sm text-warning-700">
      <HugeiconsIcon
        icon={AlertCircleIcon}
        size={18}
        className="mt-0.5 shrink-0"
      />
      <p>{warningNoteFor(item)}</p>
    </div>
  );

  return (
    <form
      onSubmit={onSubmit({ onSuccess: (formValues) => convert(formValues) })}
      className="flex h-full flex-col"
    >
      <div className="flex items-start justify-between border-b border-alpha-40 px-6 py-4">
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-content-1000">
            Review parsed email
          </h2>
          <Badge variant={reasonBadge.variant} size={BadgeSize.SM}>
            {reasonBadge.label}
          </Badge>
        </div>
        <IconButton
          aria-label="Close"
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.SM}
          HugeIcon={Cancel01Icon}
          onClick={onClose}
        />
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto bg-background-900 px-6 py-5">
        <div className="space-y-1.5">
          <p className="text-xs font-bold uppercase tracking-wide text-content-300">
            Original email
          </p>
          <div className="space-y-1 rounded-lg border border-alpha-100 bg-white p-3 text-sm text-content-700">
            <Show if={Boolean(item.subject)}>
              <p>
                <span className="font-bold text-content-1000">Subject:</span>{" "}
                {item.subject}
              </p>
            </Show>
            <Show if={Boolean(item.received_via)}>
              <p>
                <span className="font-bold text-content-1000">Via:</span>{" "}
                {item.received_via}
              </p>
            </Show>
            <p className="whitespace-pre-wrap break-words font-mono text-content-700">
              {item.original_body ?? item.message}
            </p>
          </div>
        </div>

        <BaseInput
          id="reviewName"
          label="Name"
          labelClassName="mb-1.5"
          size={InputSize.BASE}
          required
          {...register("name")}
        />

        <BaseInput
          id="reviewEmail"
          label="Email"
          labelClassName="mb-1.5"
          size={InputSize.BASE}
          placeholder="john.doe@acme.com"
          required
          {...register("email")}
          error={emailError}
        />

        <BaseInput
          id="reviewPhone"
          label="Phone"
          labelClassName="mb-1.5"
          size={InputSize.BASE}
          placeholder="(123) 456-7890"
          required
          parser={tenDigitTelParser({ asString: true })}
          {...register("phone")}
          error={phoneError}
        />

        <Show if={warningTarget === "contact"}>{warningBox}</Show>

        <TextArea
          id="reviewMessage"
          label="Message"
          labelClassName="mb-1.5"
          rows={4}
          {...register("message")}
        />

        <AsyncSelect
          id="reviewProperty"
          isClearable
          labelClassname="mb-1.5"
          label={Property}
          required
          menuPosition="fixed"
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : undefined
          }
          debounceTimeout={300}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          searchable
          value={values.property_id || ""}
          // @ts-expect-error -- loader signature is loose
          loadOptions={loadPropertyOptions}
          error={errors.property_id}
          options={
            values.property_id && propertyLabel
              ? [{ label: propertyLabel, value: values.property_id }]
              : []
          }
          onChange={(e) => {
            if (!Array.isArray(e)) {
              const nextValue = e.target.value;
              handleChange("property_id", () =>
                nextValue ? Number(nextValue) : undefined
              )();
              setPropertyLabel(e.label);
              handleChange("unit_id", () => undefined)();
              setUnitLabel(undefined);
            }
          }}
          additional={{ page: 1 }}
        />

        <Show if={warningTarget === "property"}>{warningBox}</Show>

        <AsyncSelect
          id="reviewUnit"
          isClearable
          menuPosition="fixed"
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : undefined
          }
          debounceTimeout={300}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          key={values.property_id}
          label={Unit}
          labelClassname="mb-1.5"
          required
          error={errors.unit_id}
          disabled={!values.property_id}
          placeholder={values.property_id ? undefined : "No property selected"}
          searchable
          value={values.unit_id || ""}
          options={
            values.unit_id && unitLabel
              ? [{ label: unitLabel, value: values.unit_id }]
              : []
          }
          // @ts-expect-error -- loader signature is loose
          loadOptions={loadUnitOptions}
          additional={{ page: 1, propertyId: values.property_id }}
          onChange={(e) => {
            if (!Array.isArray(e)) {
              const nextValue = e.target.value;
              handleChange("unit_id", () =>
                nextValue ? Number(nextValue) : undefined
              )();
              setUnitLabel(e.label);
            }
          }}
        />
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-alpha-40 bg-white px-6 py-4">
        <Button
          type="button"
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          disabled={isBusy}
          onClick={() => dismiss()}
        >
          Dismiss
        </Button>
        <Button
          type="submit"
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          disabled={!canSubmit || isBusy}
        >
          Create lead
        </Button>
      </div>
    </form>
  );
};

export const ReviewParsedEmailDrawer = ({
  item,
  open,
  onClose
}: {
  item: ReviewItem | null;
  open: boolean;
  onClose: () => void;
}) => (
  <Drawer
    className="rounded-l-lg"
    direction="right"
    open={open}
    onClose={onClose}
    size={480}
  >
    <Show if={Boolean(item)}>
      {item ? (
        <ReviewParsedEmailForm key={item.id} item={item} onClose={onClose} />
      ) : null}
    </Show>
  </Drawer>
);
